import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Shield } from 'lucide-react'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import Layout, { ALL_NAV_ITEMS } from './components/Layout'
import { menuPermissionsApi } from './services/api'
import LoginPage from './pages/Login'
import Dashboard from './pages/Dashboard'
import DashboardStampaA3 from './pages/DashboardStampaA3'
import CabNuovaEtichetta from './pages/cab/CabNuovaEtichetta'
import CabArchivio from './pages/cab/CabArchivio'
import CabAnagrafica from './pages/cab/CabAnagrafica'
import CabStampanti from './pages/cab/CabStampanti'
import Pratiche from './pages/Pratiche'
import WorkOrderDetail from './pages/WorkOrderDetail'
import Committenti from './pages/Committenti'
import RfiOrdini from './pages/RfiOrdini'
import RfiRapportinoDetail from './pages/RfiRapportinoDetail'
import RfiRapportinoGuerratoEditor from './pages/RfiRapportinoGuerratoEditor'
import RfiRapportini from './pages/RfiRapportini'
import RfiListino from './pages/RfiListino'
import RfiImpostazioni from './pages/RfiImpostazioni'
import ClientiFinali from './pages/ClientiFinali'
import Calendario from './pages/Calendario'
import Magazzino from './pages/Magazzino'
import Fatturazione from './pages/Fatturazione'
import Report from './pages/Report'
import Utenti from './pages/Utenti'
import Rapportini from './pages/Rapportini'
import Mappa from './pages/Mappa'
import MioTurno from './pages/MioTurno'
import Checklist from './pages/Checklist'
import DispatcherBoard from './pages/DispatcherBoard'
import Backup from './pages/Backup'
import OrariApertura from './pages/OrariApertura'
import FirmaVerbale from './pages/FirmaVerbale'
import Registrati from './pages/Registrati'
import CambioPorte from './pages/CambioPorte'
import Commesse from './pages/Commesse'
import SpeseRimborso from './pages/SpeseRimborso'
import CalendarioAssegnazioni from './pages/CalendarioAssegnazioni'
import ReportEconomico from './pages/ReportEconomico'
import ReportOreTecnici from './pages/ReportOreTecnici'
import Impostazioni from './pages/Impostazioni'
import ProfiloSicurezza from './pages/ProfiloSicurezza'
import Trasferte from './pages/Trasferte'
import TrasferteReport from './pages/TrasferteReport'
import WhatsApp from './pages/WhatsApp'
import GestionePersonale from './pages/GestionePersonale'
import FleetPage from './pages/fleet/FleetPage'
import HomeDashboard from './pages/HomeDashboard'
import GestioneUtentiExcel from './pages/GestioneUtentiExcel'
import RapportinoTemplateEditor from './pages/RapportinoTemplateEditor'
import RedLockGuard from './components/red/RedLockGuard'
import RedPortfolio from './pages/red/RedPortfolio'
import RedImmobileDetail from './pages/red/RedImmobileDetail'
import RedDashboard from './pages/red/RedDashboard'
import RedOpportunita from './pages/red/RedOpportunita'
import RedGantt from './pages/red/RedGantt'
import RedMappaPortfolio from './pages/red/RedMappaPortfolio'
import RedRubricaProfessionisti from './pages/red/RedRubricaProfessionisti'
import RedPraticheUSR from './pages/red/RedPraticheUSR'
import RedImpostazioni from './pages/red/RedImpostazioni'
import RedAgenda from './pages/red/RedAgenda'
import RedConfermaScadenza from './pages/red/RedConfermaScadenza'
import TimsurveyPratiche from './pages/timsurvey/TimsurveyPratiche'
import TimsurveyNuovaPratica from './pages/timsurvey/TimsurveyNuovaPratica'
import TimsurveyDettaglio from './pages/timsurvey/TimsurveyDettaglio'
import TimsurveyWizard from './pages/timsurvey/TimsurveyWizard'

function RoleBasedHome() {
  const { user } = useAuth()
  if (['tecnico', 'tecnico_senior'].includes(user?.role ?? '')) {
    return <Navigate to="/mio-turno" replace />
  }
  return <HomeDashboard />
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-t-[#0A84FF] rounded-full animate-spin" style={{ borderColor: 'rgba(84,84,88,0.4)', borderTopColor: '#0A84FF' }} />
        <p className="text-white/60 text-sm">Caricamento XSophia...</p>
      </div>
    </div>
  )
}

function AccessoNegato() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
      <Shield size={40} style={{ opacity: 0.4 }} />
      <h2 className="text-lg font-semibold">Accesso non consentito</h2>
      <p className="text-sm text-gray-500 max-w-md">
        Non disponi dei permessi necessari per visualizzare questa pagina. Contatta un amministratore
        se ritieni che si tratti di un errore.
      </p>
    </div>
  )
}

/**
 * Trova, tra tutte le voci di menu note (`ALL_NAV_ITEMS`), quella che meglio
 * corrisponde al pathname corrente — match esatto o prefisso di segmento
 * (es. `/pratiche/123` → voce `/pratiche`). L'array è già ordinato per
 * lunghezza di path decrescente, quindi il primo match trovato è il più specifico.
 */
function findMenuItemForPath(pathname: string) {
  return ALL_NAV_ITEMS.find(item =>
    pathname === item.path || pathname.startsWith(item.path + '/')
  )
}

/** Rotta protetta da autenticazione ma senza il Layout (sidebar/topbar) —
 * usata per viste a sé stanti pensate per la stampa. */
function AuthOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  const location = useLocation()
  if (isLoading) return <LoadingScreen />
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />
  return <>{children}</>
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  const location = useLocation()

  const { data: myMenus, isLoading: menusLoading } = useQuery({
    queryKey: ['my-menus', user?.id],
    queryFn: () => menuPermissionsApi.myMenus().then(r => r.data as { id: string; route: string }[]),
    enabled: !!user,
    staleTime: 60_000,
  })

  if (isLoading) return <LoadingScreen />
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />

  // Se l'utente ha permessi menu granulari configurati (myMenus non vuoto), la rotta
  // richiesta deve corrispondere a un menu abilitato — stesso criterio già applicato
  // alla sidebar in Layout.tsx (`menuChildVisible` / filtro NAV_ITEMS), ma qui
  // enforced anche su navigazione diretta/link (es. frazionario in un rapportino
  // che punta a /pratiche/:id), non solo sul click in sidebar.
  if (menusLoading) return <LoadingScreen />
  if (myMenus && myMenus.length > 0) {
    const menuItem = findMenuItemForPath(location.pathname)
    if (menuItem && !myMenus.some(m => m.route === menuItem.path)) {
      return <Layout><AccessoNegato /></Layout>
    }
  }

  return <Layout>{children}</Layout>
}

function AppRoutes() {
  const { user } = useAuth()
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/" element={<ProtectedRoute><RoleBasedHome /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/dashboard/stampa-a3" element={<AuthOnlyRoute><DashboardStampaA3 /></AuthOnlyRoute>} />
      <Route path="/pratiche" element={<ProtectedRoute><Pratiche /></ProtectedRoute>} />
      <Route path="/pratiche/:id" element={<ProtectedRoute><WorkOrderDetail /></ProtectedRoute>} />
      <Route path="/committenti" element={<ProtectedRoute><Committenti /></ProtectedRoute>} />
      <Route path="/rfi" element={<ProtectedRoute><RfiOrdini /></ProtectedRoute>} />
      <Route path="/rfi/ordini/:id" element={<ProtectedRoute><RfiRapportinoDetail /></ProtectedRoute>} />
      <Route path="/rfi/rapportino-guerrato/:id" element={<ProtectedRoute><RfiRapportinoGuerratoEditor /></ProtectedRoute>} />
      <Route path="/rfi/rapportini" element={<ProtectedRoute><RfiRapportini /></ProtectedRoute>} />
      <Route path="/rfi/listino" element={<ProtectedRoute><RfiListino /></ProtectedRoute>} />
      <Route path="/rfi/impostazioni" element={<ProtectedRoute><RfiImpostazioni /></ProtectedRoute>} />
      <Route path="/clienti" element={<ProtectedRoute><ClientiFinali /></ProtectedRoute>} />
      <Route path="/calendario" element={<ProtectedRoute><Calendario /></ProtectedRoute>} />
      <Route path="/magazzino" element={<ProtectedRoute><Magazzino /></ProtectedRoute>} />
      <Route path="/fatturazione" element={<ProtectedRoute><Fatturazione /></ProtectedRoute>} />
      <Route path="/report" element={<ProtectedRoute><Report /></ProtectedRoute>} />
      <Route path="/utenti" element={<ProtectedRoute><GestioneUtentiExcel /></ProtectedRoute>} />
      <Route path="/utenti-classico" element={<ProtectedRoute><Utenti /></ProtectedRoute>} />
      <Route path="/rapportini" element={<ProtectedRoute><Rapportini /></ProtectedRoute>} />
      <Route path="/mappa" element={<ProtectedRoute><Mappa /></ProtectedRoute>} />
      <Route path="/mio-turno" element={<ProtectedRoute><MioTurno /></ProtectedRoute>} />
      <Route path="/checklist" element={<ProtectedRoute><Checklist /></ProtectedRoute>} />
      <Route path="/dispatcher" element={<ProtectedRoute><DispatcherBoard /></ProtectedRoute>} />
      <Route path="/backup" element={<ProtectedRoute><Backup /></ProtectedRoute>} />
      <Route path="/orari-apertura" element={<ProtectedRoute><OrariApertura /></ProtectedRoute>} />
      <Route path="/commesse" element={<ProtectedRoute><Commesse /></ProtectedRoute>} />
      <Route path="/spese-rimborso" element={<ProtectedRoute><SpeseRimborso /></ProtectedRoute>} />
      <Route path="/calendario-assegnazioni" element={<ProtectedRoute><CalendarioAssegnazioni /></ProtectedRoute>} />
      <Route path="/report-ore-tecnici" element={<ProtectedRoute><ReportOreTecnici /></ProtectedRoute>} />
      <Route path="/report-economico" element={<ProtectedRoute><ReportEconomico /></ProtectedRoute>} />
      <Route path="/impostazioni" element={<ProtectedRoute><Impostazioni /></ProtectedRoute>} />
      <Route path="/rapportino-templates" element={<ProtectedRoute><RapportinoTemplateEditor /></ProtectedRoute>} />
      <Route path="/profilo" element={<ProtectedRoute><ProfiloSicurezza /></ProtectedRoute>} />
      <Route path="/trasferte" element={<ProtectedRoute><Trasferte /></ProtectedRoute>} />
      <Route path="/trasferte/report" element={<ProtectedRoute><TrasferteReport /></ProtectedRoute>} />
      <Route path="/gestione-personale" element={<ProtectedRoute><GestionePersonale /></ProtectedRoute>} />
<Route path="/fleet" element={<ProtectedRoute><FleetPage /></ProtectedRoute>} />
<Route path="/red/dashboard" element={<ProtectedRoute><RedLockGuard><RedDashboard /></RedLockGuard></ProtectedRoute>} />
<Route path="/red" element={<ProtectedRoute><RedLockGuard><RedPortfolio /></RedLockGuard></ProtectedRoute>} />
<Route path="/red/opportunita" element={<ProtectedRoute><RedLockGuard><RedOpportunita /></RedLockGuard></ProtectedRoute>} />
<Route path="/red/mappa" element={<ProtectedRoute><RedLockGuard><RedMappaPortfolio /></RedLockGuard></ProtectedRoute>} />
<Route path="/red/gantt" element={<ProtectedRoute><RedLockGuard><RedGantt /></RedLockGuard></ProtectedRoute>} />
<Route path="/red/professionisti" element={<ProtectedRoute><RedLockGuard><RedRubricaProfessionisti /></RedLockGuard></ProtectedRoute>} />
<Route path="/red/pratiche-usr" element={<ProtectedRoute><RedLockGuard><RedPraticheUSR /></RedLockGuard></ProtectedRoute>} />
<Route path="/red/impostazioni" element={<ProtectedRoute><RedLockGuard><RedImpostazioni /></RedLockGuard></ProtectedRoute>} />
<Route path="/red/agenda" element={<ProtectedRoute><RedLockGuard><RedAgenda /></RedLockGuard></ProtectedRoute>} />
<Route path="/red/:id" element={<ProtectedRoute><RedLockGuard><RedImmobileDetail /></RedLockGuard></ProtectedRoute>} />
<Route path="/cab" element={<ProtectedRoute><CabNuovaEtichetta /></ProtectedRoute>} />
<Route path="/cab/archivio" element={<ProtectedRoute><CabArchivio /></ProtectedRoute>} />
<Route path="/cab/anagrafica" element={<ProtectedRoute><CabAnagrafica /></ProtectedRoute>} />
<Route path="/cab/stampanti" element={<ProtectedRoute><CabStampanti /></ProtectedRoute>} />
<Route path="/timsurvey" element={<ProtectedRoute><TimsurveyPratiche /></ProtectedRoute>} />
<Route path="/timsurvey/nuova" element={<ProtectedRoute><TimsurveyNuovaPratica /></ProtectedRoute>} />
<Route path="/timsurvey/:id" element={<ProtectedRoute><TimsurveyDettaglio /></ProtectedRoute>} />
<Route path="/timsurvey/:id/wizard" element={<ProtectedRoute><TimsurveyWizard /></ProtectedRoute>} />
      <Route path="/whatsapp" element={<ProtectedRoute><WhatsApp /></ProtectedRoute>} />
      {/* Firma verbale — pagina pubblica senza login */}
      <Route path="/firma-verbale/:token" element={<FirmaVerbale />} />
      <Route path="/registrati" element={<Registrati />} />
      <Route path="/red/conferma/:token" element={<RedConfermaScadenza />} />
      <Route path="/cambio-porte" element={<CambioPorte />} />
      {/* RLS Votazione — modulo standalone, pagine pubbliche senza login XSophia */}
      {/* Redirect vecchie rotte */}
      <Route path="/work-orders" element={<Navigate to="/pratiche" replace />} />
      <Route path="/work-orders/:id" element={<Navigate to="/pratiche" replace />} />
      <Route path="/tecnici" element={<Navigate to="/utenti" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  )
}
